import { Skeleton } from '@dnb/eufemia';
import { Form } from '@dnb/eufemia/extensions/forms';
import {
  OAuthIntegrationInfoSchema,
  OAuthIntegrationInfoWithClassificationSchema,
  ServiceNowInfoWithClassificationInputDto,
} from '@portals/shared/portal/ApiDto';
import { ApiError } from '@portals/shared-frontend/utils/ApiError';
import { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { makeApiAvailableForOauth } from '@/api/app';
import LoadingModal from '@/components/LoadingModal';
import Toast from '@/components/Toast';
import { useBackend } from '@/hooks/useBackend';
import { ApiProviderPageOutletContext } from '@/pages/profile/apps/application/ApiProviderPage';
import { SwitchContainerMode } from '@/types/SwitchContainerModeTypes';

import { SERVICE_NOW_INFO_FORM_ID } from './constants';
import EditContainer from './EditContainer';
import ViewContainer from './ViewContainer';

export type ServiceNowInfoFormData = Partial<ServiceNowInfoWithClassificationInputDto>;

export default function ServiceNowInfoSection(): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { set } = Form.useData<ServiceNowInfoFormData>(SERVICE_NOW_INFO_FORM_ID);

  const { data: serviceDomains, isLoading: serviceDomainsIsLoading } = useBackend('/service-now/service-domains');
  const { api, isTeamAdmin, mutate } = useOutletContext<ApiProviderPageOutletContext>();

  const switchContainerModeRef = useRef<SwitchContainerMode>(undefined);

  const defaultData: ServiceNowInfoFormData = useMemo(
    () => ({
      serviceNowIdentifier: api.serviceNowIdentifier ?? undefined,
      serviceNowServiceDomainId: api.serviceNowServiceDomainId,
      severityClassification: api.severityClassification ?? undefined,
      piiExposed: api.piiExposed ?? undefined,
      gdprCompliant: api.gdprCompliant ?? undefined,
      classification: api.classification,
    }),
    [api],
  );

  // Update default form data when api is updated from another section (ApiDetailsSection)
  useEffect(() => {
    set(defaultData);
  }, [defaultData, set]);

  const onSubmit = useCallback(
    async (formData: ServiceNowInfoFormData) => {
      try {
        setIsSubmitting(true);
        setErrorMessage(null);
        // Omit the 'classification' field before passing to the API
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { classification, ...serviceNowInfoInput } = formData;
        const data = OAuthIntegrationInfoSchema.parse(serviceNowInfoInput);
        await makeApiAvailableForOauth(api.id, data);
        setSuccessMessage('Changes saved successfully');
        await mutate();
        switchContainerModeRef.current?.('view');
      } catch (error: unknown) {
        if (ApiError.isApiError(error)) {
          setErrorMessage(error.body.message);
        } else {
          setErrorMessage('An unexpected error occurred. Please try again later.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [api.id, mutate],
  );

  return (
    <>
      {isSubmitting && <LoadingModal />}
      <Skeleton show={serviceDomainsIsLoading}>
        {serviceDomains && (
          <Form.Handler<ServiceNowInfoFormData>
            id={SERVICE_NOW_INFO_FORM_ID}
            onSubmit={onSubmit}
            schema={OAuthIntegrationInfoWithClassificationSchema}
          >
            <Form.Section containerMode="view">
              <ViewContainer
                isTeamAdmin={isTeamAdmin}
                onEdit={() => setErrorMessage(null)}
                serviceDomains={serviceDomains}
                switchContainerModeRef={switchContainerModeRef}
              />
              <EditContainer
                errorMessage={errorMessage}
                serviceDomains={serviceDomains}
                switchContainerModeRef={switchContainerModeRef}
              />
            </Form.Section>
          </Form.Handler>
        )}
      </Skeleton>
      {successMessage && <Toast message={successMessage} position="bottom" variant="success" />}
    </>
  );
}
